import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, gql } from '@apollo/client';
import { toast } from 'react-hot-toast';
import {
  applyCompletionDataToItems,
  buildCategoriesMap,
  countRequiredComplete,
  groupChecklistItemsByCategory,
} from './job-details/checklist-utils';
import { buildJobStatusGPSInput, captureJobLocationSilently } from './job-details/job-gps-utils';

const SAVE_JOB_CHECKLIST = gql`
  mutation SaveJobChecklist($input: JobChecklistCompletionInput!) {
    saveJobChecklistCompletion(input: $input) {
      id
      status
    }
  }
`;

const SAVE_JOB_CHECKLIST_ITEM = gql`
  mutation SaveJobChecklistItem($input: JobChecklistItemSaveInput!) {
    saveJobChecklistItemCompletion(input: $input) {
      id
      itemId
      completed
    }
  }
`;

// Add mutation to mark inspection job as complete
const MARK_INSPECTION_COMPLETE = gql`
  mutation MarkInspectionComplete($id: ID!, $notes: String, $gps: JobStatusGPSInput) {
    markInspectionComplete(id: $id, notes: $notes, gps: $gps) {
      id
      status
    }
  }
`;

// Add a query to fetch all checklist categories
const GET_CHECKLIST_CATEGORIES = gql`
  query GetChecklistCategories {
    checklistCategories {
      id
      title
      color
      isActive
    }
  }
`;

// Add a query to get job status history to check for completion status
const GET_JOB_STATUS_HISTORY = gql`
  query GetJobStatusHistory($jobId: ID!) {
    jobStatusHistory(jobId: $jobId) {
      id
      status
      timestamp
      notes
    }
  }
`;

// Add a query to check checklist completion status
const GET_CHECKLIST_STATUS = gql`
  query GetInspectionChecklistsStatus($inspectionJobId: ID!) {
    inspectionChecklistsStatus(inspectionJobId: $inspectionJobId) {
      cleaningCompleted
      inspectionCompleted
    }
  }
`;

// Add a query to fetch existing checklist completions
const GET_JOB_CHECKLIST_COMPLETIONS = gql`
  query GetJobChecklistCompletions($jobId: ID!) {
    getJobChecklistCompletions(jobId: $jobId) {
      id
      jobId
      checklistId
      completedAt
      notes
      percentageScore
      items {
        id
        itemId
        completed
        notes
      }
    }
  }
`;

export default function JobChecklistModal({ isOpen, onClose, job, viewOnly = false, associatedCleaningJob = null }) {
  const [checklistItems, setChecklistItems] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [savingItemIds, setSavingItemIds] = useState({});
  
  // Add loading state for categories
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);
  
  // Detect if this is an inspection job
  const isInspectionJob = job?.category === 'INSPECTION';
  
  // Detect if we have an associated cleaning job with a checklist
  const hasAssociatedCleaningJob = Boolean(associatedCleaningJob?.serviceType?.checklist);

  // For inspection jobs with associated cleaning job, allow flexible checklist selection
  // Default to inspection checklist first, as it's typically completed first
  const [activeChecklistType, setActiveChecklistType] = useState('inspection');
  
  // Separate state for cleaning checklist items
  const [cleaningChecklistItems, setCleaningChecklistItems] = useState([]);
  
  // Add a state to track which checklists have been completed
  const [completedChecklists, setCompletedChecklists] = useState({
    cleaning: false,
    inspection: false
  });

  // Add state for storing completed checklist data to allow re-editing
  const [savedChecklistData, setSavedChecklistData] = useState({
    cleaning: null,
    inspection: null
  });

  // Add state for loading checklist status
  const [isLoadingStatus, setIsLoadingStatus] = useState(false);
  
  // Add state for completing the whole job
  const [isCompletingJob, setIsCompletingJob] = useState(false);
  
  // Determine if the checklist can be submitted based on the service type setting
  const canSubmitChecklist = job?.serviceType?.canSubmitChecklist ?? false;
  
  // If canSubmitChecklist is false or viewOnly is true, treat as read-only
  const isReadOnly = viewOnly || !canSubmitChecklist;

  // Query for job status history to check for checklist completion
  const { data: historyData, refetch: refetchHistory } = useQuery(GET_JOB_STATUS_HISTORY, {
    variables: { jobId: job?.id },
    skip: !job?.id || !isInspectionJob,
    fetchPolicy: 'network-only'
  });

  // Query for checklist completion status (for inspection jobs)
  const { data: checklistStatusData, loading: checklistStatusLoading, refetch: refetchChecklistStatus } = useQuery(GET_CHECKLIST_STATUS, {
    variables: { inspectionJobId: job?.id },
    skip: !job?.id || !isInspectionJob,
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data?.inspectionChecklistsStatus) {
        setCompletedChecklists({
          cleaning: data.inspectionChecklistsStatus.cleaningCompleted,
          inspection: data.inspectionChecklistsStatus.inspectionCompleted
        });
      }
    }
  });

  // Query for existing checklist completions for the main job
  const { data: jobChecklistCompletions, refetch: refetchJobCompletions } = useQuery(GET_JOB_CHECKLIST_COMPLETIONS, {
    variables: { jobId: job?.id },
    skip: !job?.id,
    fetchPolicy: 'network-only'
  });

  // Query for existing checklist completions for the associated cleaning job
  const { data: cleaningJobChecklistCompletions, refetch: refetchCleaningCompletions } = useQuery(GET_JOB_CHECKLIST_COMPLETIONS, {
    variables: { jobId: associatedCleaningJob?.id },
    skip: !associatedCleaningJob?.id,
    fetchPolicy: 'network-only'
  });

  // Add mutation for marking the inspection job as complete
  const [markInspectionComplete] = useMutation(MARK_INSPECTION_COMPLETE, {
    onCompleted: () => {
      setIsCompletingJob(false);
      onClose();
    },
    onError: (error) => {
      // The catch block in handleMarkInspectionComplete will handle this
    }
  });

  // Reset states when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsSubmitting(false);
      setIsCompletingJob(false);
      
      // Refetch the status data when modal opens
      if (isInspectionJob) {
        refetchHistory();
        refetchChecklistStatus();
      }
      
      // Refetch completion data when modal opens
      refetchJobCompletions();
      if (associatedCleaningJob?.id) {
        refetchCleaningCompletions();
      }
      
      // Use the checklist status data directly if available
      if (checklistStatusData?.inspectionChecklistsStatus) {
        setCompletedChecklists({
          cleaning: checklistStatusData.inspectionChecklistsStatus.cleaningCompleted,
          inspection: checklistStatusData.inspectionChecklistsStatus.inspectionCompleted
        });
      }
      // Fall back to parsing history notes if no direct status data
      else if (historyData?.jobStatusHistory && isInspectionJob) {
        const cleaningCompleted = historyData.jobStatusHistory.some(
          history => history.notes && (
            history.notes.includes('Cleaning checklist completed') ||
            history.notes.includes('cleaning checklist completed')
          )
        );
        
        const inspectionCompleted = historyData.jobStatusHistory.some(
          history => history.notes && (
            history.notes.includes('Inspection checklist completed') ||
            history.notes.includes('inspection checklist completed')
          )
        );
        
        setCompletedChecklists({
          cleaning: cleaningCompleted,
          inspection: inspectionCompleted
        });
      }
    }
  }, [isOpen, historyData, isInspectionJob, hasAssociatedCleaningJob, checklistStatusData, refetchHistory, refetchChecklistStatus, refetchJobCompletions, refetchCleaningCompletions]);

  // Fetch all categories to have a reliable lookup
  const { data: categoriesData, loading: categoriesLoading, error: categoriesError } = useQuery(GET_CHECKLIST_CATEGORIES, {
    fetchPolicy: 'network-only', // Change to network-only to ensure fresh data
    onCompleted: (data) => {
      setCategoriesLoaded(true);
    },
    onError: (error) => {
      console.warn('Failed to fetch checklist categories, using fallback:', error.message);
      // Set categories loaded to true so we can proceed with fallback
      setCategoriesLoaded(true);
    }
  });

  const [saveJobChecklistItem] = useMutation(SAVE_JOB_CHECKLIST_ITEM);
  
  // Mutation to save the checklist results
  const [saveJobChecklist] = useMutation(SAVE_JOB_CHECKLIST, {
    onCompleted: (data) => {
      // Update completion status for the current checklist
      setCompletedChecklists(prev => ({
        ...prev,
        [activeChecklistType]: true
      }));
      
      // Save the completed checklist data for potential re-editing
      const currentItems = activeChecklistType === 'inspection' ? checklistItems : cleaningChecklistItems;
      setSavedChecklistData(prev => ({
        ...prev,
        [activeChecklistType]: currentItems
      }));
      
      // Refetch completion data to ensure we have the latest
      if (activeChecklistType === 'inspection') {
        refetchJobCompletions();
      } else {
        refetchCleaningCompletions();
      }
      
      // Refetch status data
      if (isInspectionJob) {
        refetchHistory();
        refetchChecklistStatus();
      }
    },
    onError: (error) => {
      toast.error(`Error saving checklist: ${error.message}`);
      setIsSubmitting(false);
    }
  });

  // Initialize checklist items from job data
  useEffect(() => {
    if (!job?.serviceType?.checklist?.items || (!categoriesData?.checklistCategories && categoriesLoading)) {
      return;
    }
    
    try {
      const useChecklistStyle = job?.serviceType?.useChecklistStyle ?? false;
      const categoriesMap = buildCategoriesMap(
        categoriesData,
        job.serviceType.checklist.items,
        categoriesError
      );

      let groupedItems = groupChecklistItemsByCategory(
        job.serviceType.checklist.items,
        categoriesMap,
        categoriesError,
        useChecklistStyle
      );

      groupedItems = applyCompletionDataToItems(
        groupedItems,
        jobChecklistCompletions,
        job.serviceType.checklist.id,
        useChecklistStyle
      );

      setChecklistItems(groupedItems);
    } catch (error) {
      // Error initializing checklist items - will show empty state
    }
  }, [job, categoriesData, categoriesLoading, categoriesLoaded, categoriesError, jobChecklistCompletions]);

  // Initialize checklist items from associated cleaning job data
  useEffect(() => {
    if (!associatedCleaningJob?.serviceType?.checklist?.items || (!categoriesData?.checklistCategories && categoriesLoading)) {
      return;
    }
    
    try {
      const useChecklistStyle = associatedCleaningJob?.serviceType?.useChecklistStyle ?? false;
      const categoriesMap = buildCategoriesMap(
        categoriesData,
        associatedCleaningJob.serviceType.checklist.items,
        categoriesError
      );

      let groupedItems = groupChecklistItemsByCategory(
        associatedCleaningJob.serviceType.checklist.items,
        categoriesMap,
        categoriesError,
        useChecklistStyle
      );

      groupedItems = applyCompletionDataToItems(
        groupedItems,
        cleaningJobChecklistCompletions,
        associatedCleaningJob.serviceType.checklist.id,
        useChecklistStyle
      );

      setCleaningChecklistItems(groupedItems);
    } catch (error) {
      // Error initializing cleaning checklist items - will show empty state
    }
  }, [associatedCleaningJob, categoriesData, categoriesLoading, categoriesLoaded, categoriesError, cleaningJobChecklistCompletions]);

  useEffect(() => {
    if (job?.serviceType?.useChecklistStyle && checklistItems.length > 0) {
      const { completed, total } = countRequiredComplete(checklistItems, true);
      setCompletedChecklists(prev => ({
        ...prev,
        inspection: total > 0 && completed === total,
      }));
    }
  }, [checklistItems, job?.serviceType?.useChecklistStyle]);

  useEffect(() => {
    if (associatedCleaningJob?.serviceType?.useChecklistStyle && cleaningChecklistItems.length > 0) {
      const { completed, total } = countRequiredComplete(cleaningChecklistItems, true);
      setCompletedChecklists(prev => ({
        ...prev,
        cleaning: total > 0 && completed === total,
      }));
    }
  }, [cleaningChecklistItems, associatedCleaningJob?.serviceType?.useChecklistStyle]);

  // Function to load completed checklist data for re-editing
  const loadCompletedChecklistData = async (checklistType) => {
    if (!isInspectionJob || !savedChecklistData[checklistType]) return;
    
    try {
      // If we have saved data, restore it
      if (checklistType === 'inspection') {
        setChecklistItems(savedChecklistData.inspection);
      } else {
        setCleaningChecklistItems(savedChecklistData.cleaning);
      }
    } catch (error) {
      // Error loading completed checklist data - will use default state
    }
  };

  // Load completed data when switching tabs
  useEffect(() => {
    if (completedChecklists[activeChecklistType] && savedChecklistData[activeChecklistType]) {
      loadCompletedChecklistData(activeChecklistType);
    }
  }, [activeChecklistType, completedChecklists, savedChecklistData]);

  const handleItemStatus = (categoryId, itemId, newStatus) => {
    if (isReadOnly) return;
    
    if (activeChecklistType === 'inspection') {
      setChecklistItems(
        checklistItems.map(category => {
          if (category.id === categoryId) {
            return {
              ...category,
              items: category.items.map(item => {
                if (item.id === itemId) {
                  return { ...item, status: newStatus };
                }
                return item;
              })
            };
          }
          return category;
        })
      );
    } else {
      setCleaningChecklistItems(
        cleaningChecklistItems.map(category => {
          if (category.id === categoryId) {
            return {
              ...category,
              items: category.items.map(item => {
                if (item.id === itemId) {
                  return { ...item, status: newStatus };
                }
                return item;
              })
            };
          }
          return category;
        })
      );
    }
  };

  const handleUpdateNotes = (categoryId, itemId, notes) => {
    if (isReadOnly) return;
    
    if (activeChecklistType === 'inspection') {
      setChecklistItems(
        checklistItems.map(category => {
          if (category.id === categoryId) {
            return {
              ...category,
              items: category.items.map(item => {
                if (item.id === itemId) {
                  return { ...item, notes };
                }
                return item;
              })
            };
          }
          return category;
        })
      );
    } else {
      setCleaningChecklistItems(
        cleaningChecklistItems.map(category => {
          if (category.id === categoryId) {
            return {
              ...category,
              items: category.items.map(item => {
                if (item.id === itemId) {
                  return { ...item, notes };
                }
                return item;
              })
            };
          }
          return category;
        })
      );
    }
  };

  const handleChecklistItemToggle = async (categoryId, itemId, newChecked) => {
    if (isReadOnly) return;

    const activeJob = activeChecklistType === 'inspection' ? job : associatedCleaningJob;
    const currentItems = activeChecklistType === 'inspection' ? checklistItems : cleaningChecklistItems;
    const setItems = activeChecklistType === 'inspection' ? setChecklistItems : setCleaningChecklistItems;

    if (!activeJob?.serviceType?.checklist?.id) {
      toast.error('No checklist found for this job');
      return;
    }

    const previousItems = currentItems;
    setItems(
      currentItems.map(category => {
        if (category.id !== categoryId) return category;
        return {
          ...category,
          items: category.items.map(item =>
            item.id === itemId ? { ...item, checked: newChecked } : item
          )
        };
      })
    );

    setSavingItemIds(prev => ({ ...prev, [itemId]: true }));

    try {
      await saveJobChecklistItem({
        variables: {
          input: {
            jobId: activeJob.id,
            checklistId: activeJob.serviceType.checklist.id,
            itemId,
            completed: newChecked,
          }
        },
        refetchQueries: [
          { query: GET_JOB_CHECKLIST_COMPLETIONS, variables: { jobId: activeJob.id } },
          ...(isInspectionJob ? [
            { query: GET_JOB_STATUS_HISTORY, variables: { jobId: job?.id } },
            { query: GET_CHECKLIST_STATUS, variables: { inspectionJobId: job?.id } },
          ] : []),
        ],
      });

      const updatedItems = previousItems.map(category => {
        if (category.id !== categoryId) return category;
        return {
          ...category,
          items: category.items.map(item =>
            item.id === itemId ? { ...item, checked: newChecked } : item
          )
        };
      });

      const { completed, total } = countRequiredComplete(updatedItems, true);
      const isFullyComplete = total > 0 && completed === total;

      setCompletedChecklists(prev => ({
        ...prev,
        [activeChecklistType]: isFullyComplete
      }));

      if (activeChecklistType === 'inspection') {
        refetchJobCompletions();
      } else {
        refetchCleaningCompletions();
      }

      if (isInspectionJob) {
        refetchHistory();
        refetchChecklistStatus();
      }

      if (isFullyComplete) {
        const currentType = activeChecklistType === 'inspection' ? 'Inspection' : 'Cleaning';
        toast.success(`${currentType} checklist completed!`);
      }
    } catch (error) {
      setItems(previousItems);
      toast.error(`Error saving checklist item: ${error.message}`);
    } finally {
      setSavingItemIds(prev => {
        const next = { ...prev };
        delete next[itemId];
        return next;
      });
    }
  };

  const handleSubmit = async () => {
    if (isReadOnly) return;
    
    // Get the active items based on the selected checklist type
    const activeItems = activeChecklistType === 'inspection' ? checklistItems : cleaningChecklistItems;
    const activeJob = activeChecklistType === 'inspection' ? job : associatedCleaningJob;
    
    if (!activeJob?.serviceType?.checklist?.id) {
      toast.error('No checklist found for this job');
      return;
    }
    
    // Notes are optional for failed items - no validation needed
    
    // Validate that all required items have been addressed
    const requiredItems = activeItems.flatMap(category => 
      category.items.filter(item => item.isRequired)
    );
    
    if (requiredItems.some(item => !item.status)) {
      toast.error('Please address all required items');
      return;
    }

    setIsSubmitting(true);

    try {
      // Flatten all items for submission
      const completedItems = activeItems.flatMap(category => 
        category.items.map(item => ({
          itemId: item.id,
          completed: item.status === 'pass', // Convert status to completed boolean for backend compatibility
          notes: item.notes || ''
        }))
      );
      
      // Calculate percentage score based on the number of passed items
      const totalItems = completedItems.length;
      const passedItems = completedItems.filter(item => item.completed).length;
      const percentageScore = Math.round((passedItems / totalItems) * 100);

      const currentType = activeChecklistType === 'inspection' ? 'Inspection' : 'Cleaning';
      
      const checklist = {
        jobId: activeJob.id,
        checklistId: activeJob.serviceType.checklist.id,
        completedItems: completedItems,
        completedAt: new Date().toISOString(),
        notes: `${currentType} checklist completed`,
        percentageScore: percentageScore
      };
      

      
      const response = await saveJobChecklist({
        variables: {
          input: checklist
        },
        refetchQueries: [
          { query: GET_JOB_STATUS_HISTORY, variables: { jobId: job?.id } },
          { query: GET_CHECKLIST_STATUS, variables: { inspectionJobId: job?.id } },
          { query: GET_JOB_CHECKLIST_COMPLETIONS, variables: { jobId: activeJob.id } }
        ],
        errorPolicy: 'all' // Process all errors but still return any available data
      });
      
      if (response.errors) {
        // Check for auth errors
        const authError = response.errors.find(err => 
          err.message?.includes('authenticated') || 
          err.extensions?.code === 'UNAUTHENTICATED' ||
          err.message?.includes('Unauthorized') ||
          err.extensions?.response?.statusCode === 401
        );
        
        if (authError) {
          toast.error('Authentication error. Please refresh the page and try again.');
          setIsSubmitting(false);
          return;
        }
        
        // Handle other errors
        throw new Error(response.errors[0].message);
      }
      
      // If we got here, the save was successful
      // Refetch both the history and checklist status to ensure we have the latest data
      if (isInspectionJob) {
        try {
          await refetchHistory();
          await refetchChecklistStatus();
        } catch (error) {
          // Error refetching data - continue with completion
        }
      }
      
      // Mark this checklist type as completed
      setCompletedChecklists(prev => ({
        ...prev,
        [activeChecklistType]: true
      }));
      
      // Auto-switch between checklists for inspection jobs with associated cleaning jobs
      if (isInspectionJob && hasAssociatedCleaningJob) {
        if (activeChecklistType === 'cleaning') {
          // If completing a cleaning checklist, switch to inspection checklist
          setActiveChecklistType('inspection');
          setIsSubmitting(false);
          toast.success(`${currentType} checklist completed! You can now complete the inspection checklist.`);
        } else if (activeChecklistType === 'inspection' && !completedChecklists.cleaning) {
          // If completing inspection checklist but cleaning checklist not done yet, switch to cleaning
          setActiveChecklistType('cleaning');
          setIsSubmitting(false);
          toast.success(`${currentType} checklist completed! Please also complete the cleaning checklist.`);
        } else {
          // Both checklists completed or only inspection needed
          toast.success(`${currentType} checklist completed successfully`);
          setIsSubmitting(false);
        }
      } else {
        // For non-inspection jobs, close modal immediately
        // For standalone inspection jobs (inspection jobs without cleaning checklist), keep modal open
        if (isInspectionJob && !hasAssociatedCleaningJob) {
          // Standalone inspection - keep modal open so user can click "Complete Inspection"
          toast.success(`${currentType} checklist completed successfully! You can now complete the inspection.`);
          setIsSubmitting(false);
        } else {
          // Non-inspection job - close modal
          toast.success(`${currentType} checklist completed successfully`);
          setIsSubmitting(false);
          onClose();
        }
      }
    } catch (error) {
      
      // Check if this is an auth error
      if (error.message?.includes('authenticated') || 
          error.graphQLErrors?.some(e => e.extensions?.code === 'UNAUTHENTICATED') ||
          error.message?.includes('Unauthorized') ||
          error.networkError?.statusCode === 401) {
        toast.error('Session expired. Please refresh the page and try again.');
      } else {
        toast.error(`Error submitting checklist: ${error.message}`);
      }
      
      setIsSubmitting(false);
    }
  };

  // Function to handle marking the inspection job as complete
  const handleMarkInspectionComplete = async () => {
    if (!isInspectionJob) return;
    
    setIsCompletingJob(true);
    try {
      const location = await captureJobLocationSilently();
      const gps = buildJobStatusGPSInput(location);
      const response = await markInspectionComplete({
        variables: {
          id: job.id,
          notes: 'Inspection completed with all required checklists',
          gps,
        },
        errorPolicy: 'all' // Process all errors but still return any available data
      });
      
      if (response.errors) {
        // Check for auth errors
        const authError = response.errors.find(err => 
          err.message?.includes('authenticated') || 
          err.extensions?.code === 'UNAUTHENTICATED' ||
          err.message?.includes('Unauthorized') ||
          err.extensions?.response?.statusCode === 401
        );
        
        if (authError) {
          toast.error('Authentication error. Please refresh the page and try again.');
          setIsCompletingJob(false);
          return;
        }
        
        // Handle other errors
        throw new Error(response.errors[0].message);
      }
      
      // Success! The onCompleted callback will handle closing the modal
      toast.success('Inspection job marked as complete');
    } catch (error) {
      
      // Check if this is an auth error
      if (error.message?.includes('authenticated') || 
          error.graphQLErrors?.some(e => e.extensions?.code === 'UNAUTHENTICATED') ||
          error.message?.includes('Unauthorized') ||
          error.networkError?.statusCode === 401) {
        toast.error('Session expired. Please refresh the page and try again.');
      } else {
        toast.error(`Error marking job complete: ${error.message}`);
      }
      
      setIsCompletingJob(false);
    }
  };

  if (!isOpen) return null;

  const getModalTitle = () => {
    if (isInspectionJob && hasAssociatedCleaningJob) {
      return 'Inspection Checklists';
    }
    
    const baseTitle = job?.serviceType?.checklist?.title || 'Checklist';
    if (isReadOnly) {
      return baseTitle;
    }
    return 'Complete ' + baseTitle;
  };

  // Special case for inspection jobs without checklists
  const hasNoChecklist = !job?.serviceType?.checklist || !job?.serviceType?.checklist?.items?.length;
  const showInspectionMessage = isInspectionJob && hasNoChecklist;
  
  // Determine which checklist items to display
  const displayChecklistItems = activeChecklistType === 'inspection' ? checklistItems : cleaningChecklistItems;
  const displayChecklistTitle = activeChecklistType === 'inspection' 
    ? (job?.serviceType?.checklist?.title || 'Inspection Checklist') 
    : (associatedCleaningJob?.serviceType?.checklist?.title || 'Cleaning Checklist');
  const activeJob = activeChecklistType === 'inspection' ? job : associatedCleaningJob;
  const useChecklistStyle = activeJob?.serviceType?.useChecklistStyle ?? false;
  const requiredProgress = countRequiredComplete(displayChecklistItems, useChecklistStyle);

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
        <div className="px-4 sm:px-6 py-4 border-b">
          <div className="flex items-start justify-between">
            <div className="flex-1 pr-4">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
                {getModalTitle()}
              </h2>
              <p className="mt-1 text-sm text-gray-500 break-words">
                Property: {job?.property?.name} | {job?.property?.propertyCode}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 flex-shrink-0"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          {!canSubmitChecklist && !viewOnly && (
            <div className="mt-2 bg-blue-50 text-blue-700 p-2 rounded">
              This checklist is for reference only and cannot be submitted.
            </div>
          )}
          
          {/* Show completion status banner when required checklists are completed */}
          {isInspectionJob && 
           ((hasAssociatedCleaningJob && completedChecklists.cleaning && completedChecklists.inspection) ||
            (!hasAssociatedCleaningJob && completedChecklists.inspection)) && (
            <div className="mt-2 bg-green-50 text-green-700 p-2 rounded flex items-center">
              <svg className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
              {hasAssociatedCleaningJob 
                ? "Both checklists are complete! You can now mark the inspection job as complete."
                : "Inspection checklist is complete! You can now mark the inspection job as complete."
              }
            </div>
          )}
        </div>
        
        {/* Tabs - only show if there's an associated cleaning job */}
        {isInspectionJob && hasAssociatedCleaningJob && (
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px px-3 sm:px-6 overflow-x-auto">
              <button
                onClick={() => setActiveChecklistType('inspection')}
                className={`py-4 px-2 sm:px-4 border-b-2 text-sm font-medium whitespace-nowrap ${
                  activeChecklistType === 'inspection'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center">
                  <span className="flex items-center justify-center w-5 h-5 mr-2 text-xs rounded-full bg-purple-100 text-purple-800">
                    {completedChecklists.inspection ? '✓' : '○'}
                  </span>
                  <span className="hidden sm:inline">Inspection Checklist</span>
                  <span className="sm:hidden">Inspection</span>
                </div>
              </button>
              <button
                onClick={() => setActiveChecklistType('cleaning')}
                className={`ml-4 sm:ml-8 py-4 px-2 sm:px-4 border-b-2 text-sm font-medium whitespace-nowrap ${
                  activeChecklistType === 'cleaning'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center">
                  <span className="flex items-center justify-center w-5 h-5 mr-2 text-xs rounded-full bg-blue-100 text-blue-800">
                    {completedChecklists.cleaning ? '✓' : '○'}
                  </span>
                  <span className="hidden sm:inline">Cleaning Checklist</span>
                  <span className="sm:hidden">Cleaning</span>
                </div>
              </button>
            </nav>
          </div>
        )}
        
        <div className="overflow-y-auto p-4 sm:p-6 flex-grow">
          {showInspectionMessage ? (
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No checklist found</h3>
              <p className="mt-1 text-sm text-gray-500">
                This inspection job does not have a checklist associated with it.
              </p>
            </div>
          ) : (
            <>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">{displayChecklistTitle}</h3>
                {!isReadOnly && useChecklistStyle && requiredProgress.total > 0 && (
                  <div className="text-sm text-gray-600">
                    Progress: {requiredProgress.completed} / {requiredProgress.total} required complete
                  </div>
                )}
                {!isReadOnly && !useChecklistStyle && (
                  <div className="text-sm text-gray-600">
                    Score: {(() => {
                      const allItems = displayChecklistItems.flatMap(category => category.items);
                      const totalItems = allItems.length;
                      if (totalItems === 0) return "N/A";
                      
                      const passedItems = allItems.filter(item => item.status === 'pass').length;
                      const percentageScore = Math.round((passedItems / totalItems) * 100);
                      
                      return `${percentageScore}%`;
                    })()}
                  </div>
                )}
              </div>
          
              {displayChecklistItems.length === 0 ? (
                <div className="text-center py-12">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No checklist items found</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    This {activeChecklistType} does not have any items in its checklist.
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {displayChecklistItems.map((category) => (
                    <div key={category.id} className="border rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 mb-3">{category.title}</h4>
                      <ul className="space-y-3">
                        {category.items.map((item) => (
                          <li key={item.id} className="border-b border-gray-200 pb-3 last:border-0 last:pb-0">
                            <div className="flex items-start space-x-3">
                              {useChecklistStyle && (
                                <div className="flex-shrink-0 pt-1">
                                  <input
                                    type="checkbox"
                                    checked={Boolean(item.checked)}
                                    onChange={(e) => handleChecklistItemToggle(category.id, item.id, e.target.checked)}
                                    disabled={isReadOnly || Boolean(savingItemIds[item.id])}
                                    className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 disabled:opacity-50"
                                    aria-label={`Mark ${item.title} as complete`}
                                  />
                                </div>
                              )}
                              <div className="flex-1">
                                <div className="flex justify-between">
                                  <label className={`font-medium ${item.checked && useChecklistStyle ? 'text-gray-500 line-through' : 'text-gray-700'}`}>
                                    {item.title}
                                    {item.isRequired && <span className="text-red-500 ml-1">*</span>}
                                  </label>
                                  {useChecklistStyle && savingItemIds[item.id] && (
                                    <span className="text-xs text-gray-400">Saving...</span>
                                  )}
                                </div>
                                {item.description && (
                                  <p className="text-sm text-gray-500 mt-1">{item.description}</p>
                                )}
                                
                                {!useChecklistStyle && !isReadOnly && (
                                  <div className="mt-2 flex space-x-2">
                                    <button
                                      type="button"
                                      onClick={() => handleItemStatus(category.id, item.id, 'pass')}
                                      className={`px-3 py-1 text-sm font-medium rounded-md ${
                                        item.status === 'pass'
                                          ? 'bg-green-500 text-white'
                                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                      } ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
                                      disabled={isReadOnly}
                                    >
                                      Pass
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleItemStatus(category.id, item.id, 'fail')}
                                      className={`px-3 py-1 text-sm font-medium rounded-md ${
                                        item.status === 'fail'
                                          ? 'bg-red-500 text-white'
                                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                      } ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
                                      disabled={isReadOnly}
                                    >
                                      Fail
                                    </button>
                                    
                                    {(!isReadOnly && item.status === 'fail') && (
                                      <div className="mt-2">
                                        <input
                                          type="text"
                                          placeholder="Add notes explaining the failure (optional)"
                                          className="w-full border border-gray-300 rounded-md shadow-sm px-3 py-2 text-sm"
                                          value={item.notes || ''}
                                          onChange={(e) => handleUpdateNotes(category.id, item.id, e.target.value)}
                                        />
                                      </div>
                                    )}
                                    
                                    {isReadOnly && item.notes && (
                                      <div className="mt-2 text-sm text-gray-700 bg-gray-50 p-2 rounded">
                                        <span className="font-medium">Notes:</span> {item.notes}
                                      </div>
                                    )}
                                  </div>
                                )}
                                
                                {!useChecklistStyle && isReadOnly && (
                                  <div className="mt-2">
                                    <div className="flex items-center space-x-3">
                                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                        item.status === 'pass' 
                                          ? 'bg-green-100 text-green-800' 
                                          : 'bg-red-100 text-red-800'
                                      }`}>
                                        {item.status === 'pass' ? 'Pass' : 'Fail'}
                                      </span>
                                    </div>
                                  </div>
                                )}

                                {useChecklistStyle && isReadOnly && (
                                  <div className="mt-2">
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                      item.checked
                                        ? 'bg-green-100 text-green-800'
                                        : 'bg-gray-100 text-gray-800'
                                    }`}>
                                      {item.checked ? 'Complete' : 'Incomplete'}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
        
        <div className="px-4 sm:px-6 py-4 border-t">
          <div className="flex flex-col space-y-3 sm:flex-row sm:space-y-0 sm:space-x-3 sm:justify-end">
            <button
              onClick={onClose}
              className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              {isReadOnly ? 'Close' : useChecklistStyle ? 'Update' : 'Cancel'}
            </button>
            
            {/* Show Mark Job Complete button for inspection jobs when required checklists are completed */}
            {isInspectionJob && !isReadOnly && 
             ((hasAssociatedCleaningJob && completedChecklists.cleaning && completedChecklists.inspection) ||
              (!hasAssociatedCleaningJob && completedChecklists.inspection)) && (
              <button
                onClick={handleMarkInspectionComplete}
                disabled={isCompletingJob}
                className="w-full sm:w-auto px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCompletingJob ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Completing Job...
                  </span>
                ) : (
                  'Mark Inspection Complete'
                )}
              </button>
            )}
            
            {/* Show submit button if checklist exists and not in read-only mode */}
            {!isReadOnly && displayChecklistItems.length > 0 && !useChecklistStyle && (
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="w-full sm:w-auto px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Submitting...
                  </span>
                ) : (
                  completedChecklists[activeChecklistType] 
                    ? `Update ${activeChecklistType === 'inspection' ? 'Inspection' : 'Cleaning'} Checklist`
                    : `Save ${activeChecklistType === 'inspection' ? 'Inspection' : 'Cleaning'} Checklist`
                )}
              </button>
            )}
            
            {/* Show helpful status message for dual checklist jobs */}
            {isInspectionJob && hasAssociatedCleaningJob && !isReadOnly && (
              <div className="text-sm text-gray-600 mt-3 text-center sm:text-right sm:mt-2">
                {completedChecklists.cleaning && completedChecklists.inspection ? (
                  <span className="text-green-600 flex items-center justify-center sm:justify-end">
                    <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    Both checklists completed
                  </span>
                ) : (
                  <span>
                    Progress: {(completedChecklists.cleaning ? 1 : 0) + (completedChecklists.inspection ? 1 : 0)}/2 checklists completed
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
} 